import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAbandonAttempt, useAttempt, useScenarioAction, type ScenarioAction } from "../api/queries";
import type { DailyCount, Scenario, ScenarioListItem } from "../api/types";
import { absoluteTime } from "../lib/format";
import { pct, score, STATUS_LABELS } from "../lib/sim";
import { Button, Empty, ErrorBanner, Loading, Modal, RelativeTime, StatusBadge } from "./ui";

// A horizontal bar for a 0–100 value; `warn` colours it red.
export function PctBar({ value, warn = false }: { value: number | null; warn?: boolean }) {
  const width = Math.max(0, Math.min(100, value ?? 0));
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 flex-1 overflow-hidden rounded bg-gray-100">
        <div className={`h-full rounded ${warn ? "bg-red-500" : "bg-gray-800"}`} style={{ width: `${width}%` }} />
      </div>
      <span className="w-12 text-right text-xs tabular-nums text-gray-600">{pct(value)}</span>
    </div>
  );
}

// Column chart of started vs completed per day.
export function DailyChart({ daily }: { daily: DailyCount[] }) {
  const max = Math.max(1, ...daily.map((d) => d.started));
  if (!daily.some((d) => d.started || d.completed)) return <Empty>No attempts in the last {daily.length} days.</Empty>;
  return (
    <div>
      <div className="flex h-32 items-end gap-px" role="img" aria-label="Attempts per day">
        {daily.map((d) => (
          <div
            key={d.date}
            className="relative flex h-full flex-1 items-end"
            title={`${d.date}: ${d.started} started, ${d.completed} completed`}
          >
            <div className="w-full rounded-t bg-gray-300" style={{ height: `${(d.started / max) * 100}%` }} />
            <div
              className="absolute bottom-0 left-0 w-full rounded-t bg-gray-800"
              style={{ height: `${(d.completed / max) * 100}%` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-xs text-gray-500">
        <span>{daily[0]?.date}</span>
        <span className="flex gap-3">
          <span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-gray-300" />Started</span>
          <span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-gray-800" />Completed</span>
        </span>
        <span>{daily[daily.length - 1]?.date}</span>
      </div>
    </div>
  );
}

const CONFIRM: Partial<Record<ScenarioAction, string>> = {
  unpublish: "Move this scenario back to draft? Learners won't be able to start it, but attempts in progress can finish.",
  archive: "Archive this scenario? Learners won't be able to start it and it can no longer be edited. Its results are kept.",
  delete: "Delete this scenario permanently? This only works if no learner has attempted it.",
};

// Publish/unpublish/duplicate/archive/delete for one scenario, showing only
// the transitions its status allows. Publish validation errors (422) list
// every problem, so they're rendered as a list.
export function ScenarioActions({ scenario, compact = false, only, onDeleted }: {
  scenario: ScenarioListItem | Scenario;
  compact?: boolean;
  only?: ScenarioAction[]; // limit which actions appear (e.g. in table rows)
  onDeleted?: () => void;
}) {
  const action = useScenarioAction();
  const navigate = useNavigate();
  const [pending, setPending] = useState<ScenarioAction | null>(null);

  const run = (a: ScenarioAction) => {
    if (CONFIRM[a] && !window.confirm(CONFIRM[a])) return;
    setPending(a);
    action.mutate(
      { id: scenario.id, action: a },
      {
        onSuccess: (res) => {
          if (a === "duplicate") navigate(`/sim-patients/${(res as Scenario).id}/edit`);
          if (a === "delete") onDeleted?.();
        },
        onSettled: () => setPending(null),
      }
    );
  };

  const s = scenario.status;
  const size = compact ? "px-2 py-1 text-xs" : "";
  const buttons: { key: ScenarioAction; label: string; show: boolean; variant?: "primary" | "danger" }[] = [
    { key: "publish", label: "Publish", show: s === "draft", variant: "primary" },
    { key: "unpublish", label: "Unpublish", show: s === "published" },
    { key: "duplicate", label: "Duplicate", show: true },
    { key: "archive", label: "Archive", show: s !== "archived" },
    { key: "delete", label: "Delete", show: scenario.attempt_count === 0, variant: "danger" },
  ];

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {buttons.filter((b) => b.show && (!only || only.includes(b.key))).map((b) => (
          <Button
            key={b.key}
            variant={b.variant}
            className={size}
            loading={pending === b.key}
            disabled={pending !== null}
            onClick={() => run(b.key)}
          >
            {b.label}
          </Button>
        ))}
      </div>
      {action.error && <ActionErrors error={action.error} />}
    </div>
  );
}

export function ActionErrors({ error }: { error: unknown }) {
  const errors = (error as { errors?: string[] })?.errors ?? [];
  if (errors.length > 1 || !(error instanceof Error)) {
    return (
      <div role="alert" className="mt-2 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <p className="font-medium">Fix these before publishing:</p>
        <ul className="mt-1 list-disc pl-5">
          {errors.map((e) => <li key={e}>{e}</li>)}
        </ul>
      </div>
    );
  }
  return <div className="mt-2"><ErrorBanner error={error} /></div>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
      {children}
    </section>
  );
}

// Everything about one attempt: outcome per decision point, rubric scores,
// and the full conversation with scoring events marked inline.
export function AttemptModal({ attemptId, onClose }: { attemptId: number; onClose: () => void }) {
  const attempt = useAttempt(attemptId);
  const abandon = useAbandonAttempt();
  const a = attempt.data;

  // Decision points each user message hit, keyed by the learner's text.
  const hitsByInput = new Map<string, string[]>();
  for (const e of a?.events ?? []) {
    if (e.event_type === "decision_point_hit" && e.decision_point_description) {
      hitsByInput.set(e.student_input, [...(hitsByInput.get(e.student_input) ?? []), e.decision_point_description]);
    }
  }

  return (
    <Modal
      wide
      title={a ? `${a.scenario_title} · attempt #${a.id}` : `Attempt #${attemptId}`}
      onClose={onClose}
      footer={
        a?.status === "in_progress" && (
          <Button
            loading={abandon.isPending}
            onClick={() => {
              if (window.confirm("Close this attempt without scoring it? The learner won't be able to continue it.")) {
                abandon.mutate(a.id);
              }
            }}
          >
            Mark abandoned
          </Button>
        )
      }
    >
      <ErrorBanner error={attempt.error ?? abandon.error} />
      {attempt.isLoading ? <Loading /> : a && (
        <div className="space-y-6">
          <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
            <div><dt className="text-xs text-gray-500">Learner</dt><dd className="truncate">{a.user_full_name ?? a.user_email ?? `user ${a.user_id}`}</dd></div>
            <div><dt className="text-xs text-gray-500">Status</dt><dd><StatusBadge status={a.status} label={STATUS_LABELS[a.status]} /></dd></div>
            <div><dt className="text-xs text-gray-500">Score</dt><dd className="tabular-nums">{score(a.total_score, a.max_possible_score)} {a.score_pct !== null && <span className="text-gray-500">({pct(a.score_pct)})</span>}{a.performance_level && <div className="text-xs text-gray-600">{a.performance_level}</div>}</dd></div>
            <div><dt className="text-xs text-gray-500">Started</dt><dd title={absoluteTime(a.started_at)}><RelativeTime iso={a.started_at} /></dd></div>
          </dl>

          <Section title="Decision points">
            <ul className="divide-y rounded border">
              {a.decision_points.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span className="flex items-center gap-2">
                    <span className={p.hit ? "text-green-600" : p.is_critical ? "text-red-600" : "text-gray-400"} aria-hidden>
                      {p.hit ? "✓" : "✕"}
                    </span>
                    <span>{p.description}</span>
                    {p.is_critical && <span className="rounded bg-red-50 px-1.5 text-xs text-red-700">critical</span>}
                  </span>
                  <span className="tabular-nums text-gray-600">{p.score_awarded}/{p.max_score}</span>
                </li>
              ))}
            </ul>
          </Section>

          {a.rubric_scores.length > 0 && (
            <Section title="Rubric">
              <ul className="space-y-2">
                {a.rubric_scores.map((r) => (
                  <li key={r.criterion} className="text-sm">
                    <div className="flex justify-between"><span>{r.criterion}</span><span className="tabular-nums">{r.score}/{r.max_score}</span></div>
                    <PctBar value={r.max_score ? (100 * r.score) / r.max_score : null} />
                    {r.feedback && <p className="mt-1 text-xs text-gray-500">{r.feedback}</p>}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <Section title={`Conversation (${a.turn_count} learner turns)`}>
            {!a.messages.length ? <Empty>No messages.</Empty> : (
              <ol className="space-y-3">
                {a.messages.map((m) => {
                  const learner = m.role === "user";
                  const hits = learner ? hitsByInput.get(m.content) : undefined;
                  return (
                    <li key={m.id} className={`flex ${learner ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${learner ? "bg-gray-900 text-white" : "bg-gray-100"}`}>
                        <div className="whitespace-pre-wrap">{m.content}</div>
                        {hits && (
                          <div className="mt-1 text-xs text-green-300">Hit: {hits.join(", ")}</div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </Section>
        </div>
      )}
    </Modal>
  );
}
